import { useCallback, useEffect, useRef } from 'react';

import { useApiWebSocketConnection } from './useApiWebSocketConnection';

interface SignalRRefreshOptions {
  patientId?: number;
  locationId?: number; // For location-specific components
  examId?: number; // For exam-specific components
  worklistId?: number; // For worklist-specific components

  // Legacy handlers (keep for backward compatibility)
  onPatientFileUploaded?: () => void;
  onWorklistUpdated?: () => void;
  onFormUpdated?: () => void;
  onMessageReceived?: () => void;
  onDataUpdated?: () => void;

  // NEW: Core real-time event handlers
  onFileChanged?: (payload: { patientId?: number; fileId?: number; action?: string }) => void;
  onPatientChanged?: (payload: { patientId?: number; action?: string }) => void;
  onExamChanged?: (payload: { patientId?: number; examId?: number; action?: string; status?: string }) => void;
  onSystemDataChanged?: (payload: { dataType?: string; worklistId?: number; timestamp?: string }) => void;
  onLocationChanged?: (payload: { locationId?: number; action?: string }) => void;
  onFormChanged?: (payload: { patientId?: number; formId?: number; action?: string }) => void;
  onUserPermissionsChanged?: (payload: { userId?: string }) => void;

  // Generic refresh handlers (for convenience)
  onFileDataChanged?: () => void; // Generic file data changes
  onPatientDataChanged?: () => void; // Generic patient data changes
  onExamDataChanged?: () => void; // Generic exam data changes
  onLocationDataChanged?: () => void; // Generic location data changes

  // AI status tracking handlers
  onAIStatusUpdate?: (payload: {
    id: string;
    type: string;
    status: string;
    patientId?: number;
    fileId?: number;
    templateId?: number;
    worklistId?: number;
    progress?: number;
    message?: string;
    errorMessage?: string;
    startTime?: string;
    endTime?: string;
    result?: any;
    timestamp: string;
  }) => void;

  // Configuration options
  enableOptimizedUpdates?: boolean; // Whether to use StartViewingPatient/StopViewingPatient
  enableDebugLogging?: boolean; // Whether to log events for debugging
}

export const useSignalRRefresh = (options: SignalRRefreshOptions) => {
  const connection = useApiWebSocketConnection();
  const optionsRef = useRef(options);
  const isViewingPatientRef = useRef(false);

  // Keep options ref updated
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Log connection status changes
  useEffect(() => {
    if (options.enableDebugLogging !== false && connection) {
      console.log('[SignalR] Connection established:', {
        connectionId: connection.connectionId,
        state: connection.state,
        patientId: options.patientId,
        locationId: options.locationId,
        examId: options.examId,
        worklistId: options.worklistId,
        hasFileChangedHandler: !!options.onFileChanged,
        hasPatientChangedHandler: !!options.onPatientChanged,
        hasExamChangedHandler: !!options.onExamChanged,
        hasSystemChangedHandler: !!options.onSystemDataChanged,
        hasLocationChangedHandler: !!options.onLocationChanged,
        hasFormChangedHandler: !!options.onFormChanged,
        hasUserPermissionsChangedHandler: !!options.onUserPermissionsChanged,
        enableOptimizedUpdates: options.enableOptimizedUpdates,
      });
    } else if (options.enableDebugLogging !== false) {
      console.log('[SignalR] No connection available for patient:', options.patientId);
    }
  }, [
    connection,
    options.patientId,
    options.locationId,
    options.examId,
    options.worklistId,
    options.onFileChanged,
    options.onPatientChanged,
    options.onExamChanged,
    options.onSystemDataChanged,
    options.onLocationChanged,
    options.onFormChanged,
    options.onUserPermissionsChanged,
    options.enableOptimizedUpdates,
    options.enableDebugLogging,
  ]);

  // START/STOP viewing patient for optimized updates
  useEffect(() => {
    if (!connection || !options.enableOptimizedUpdates || !options.patientId) {
      return;
    }

    const startViewing = async () => {
      try {
        if (options.enableDebugLogging !== false) {
          console.log(`[SignalR] Starting to view patient ${options.patientId}`);
        }
        await connection.send('StartViewingPatient', options.patientId);
        isViewingPatientRef.current = true;
      } catch (error) {
        console.error('[SignalR] Failed to start viewing patient:', error);
      }
    };

    const stopViewing = async () => {
      if (isViewingPatientRef.current) {
        try {
          if (options.enableDebugLogging !== false) {
            console.log(`[SignalR] Stopping viewing patient ${options.patientId}`);
          }
          await connection.send('StopViewingPatient', options.patientId);
          isViewingPatientRef.current = false;
        } catch (error) {
          console.error('[SignalR] Failed to stop viewing patient:', error);
        }
      }
    };

    startViewing();

    return () => {
      stopViewing();
    };
  }, [connection, options.patientId, options.enableOptimizedUpdates, options.enableDebugLogging]);

  // Legacy event handlers (keep for backward compatibility)
  const handlePatientFileUploaded = useCallback((payload: { patientId: number }) => {
    const { patientId, onPatientFileUploaded, onFileDataChanged, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] patientFileUploaded event received:', {
        eventPayload: payload,
        currentPatientId: patientId,
        hasLegacyHandler: !!onPatientFileUploaded,
        hasGenericHandler: !!onFileDataChanged,
        willExecute: payload?.patientId && (patientId === undefined || payload.patientId === patientId),
      });
    }

    if (payload?.patientId && (patientId === undefined || payload.patientId === patientId)) {
      if (enableDebugLogging !== false) {
        console.log(`[SignalR] ✅ Executing file upload refresh for patient ${payload.patientId}`);
      }
      onPatientFileUploaded?.();
      onFileDataChanged?.(); // Also trigger generic file data refresh
    }
  }, []);

  const handleWorklistUpdated = useCallback((payload: { worklistId?: number; patientId?: number }) => {
    const { patientId, worklistId, onWorklistUpdated, onSystemDataChanged, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] worklistUpdated event received:', {
        eventPayload: payload,
        currentPatientId: patientId,
        currentWorklistId: worklistId,
        hasLegacyHandler: !!onWorklistUpdated,
        hasGenericHandler: !!onSystemDataChanged,
      });
    }

    const shouldExecute =
      !payload || patientId === undefined || payload.patientId === patientId || worklistId === undefined || payload.worklistId === worklistId;

    if (shouldExecute) {
      if (enableDebugLogging !== false) {
        console.log('[SignalR] ✅ Executing worklist update refresh');
      }
      onWorklistUpdated?.();
      onSystemDataChanged?.({ dataType: 'worklist', worklistId: payload?.worklistId, timestamp: new Date().toISOString() });
    }
  }, []);

  const handleFormUpdated = useCallback((payload: { formId?: number; patientId?: number }) => {
    const { patientId, onFormUpdated, onPatientDataChanged, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] formUpdated event received:', {
        eventPayload: payload,
        currentPatientId: patientId,
        hasLegacyHandler: !!onFormUpdated,
        hasGenericHandler: !!onPatientDataChanged,
      });
    }

    if (!payload || patientId === undefined || payload.patientId === patientId) {
      if (enableDebugLogging !== false) {
        console.log('[SignalR] ✅ Executing form update refresh');
      }
      onFormUpdated?.();
      onPatientDataChanged?.(); // Also trigger generic patient data refresh
    }
  }, []);

  const handleMessageReceived = useCallback((payload: unknown) => {
    const { onMessageReceived, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] messageReceived event:', {
        payload,
        hasHandler: !!onMessageReceived,
      });
    }
    onMessageReceived?.();
  }, []);

  const handleDataUpdated = useCallback((payload: unknown) => {
    const { onDataUpdated, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] dataUpdated event:', {
        payload,
        hasHandler: !!onDataUpdated,
      });
    }
    onDataUpdated?.();
  }, []);

  // NEW: Core real-time event handlers
  const handleFileChanged = useCallback((payload: { patientId?: number; fileId?: number; action?: string }) => {
    const { patientId, onFileChanged, onFileDataChanged, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] fileChanged event received:', {
        payload,
        currentPatientId: patientId,
        hasSpecificHandler: !!onFileChanged,
        hasGenericHandler: !!onFileDataChanged,
      });
    }

    if (!payload || patientId === undefined || payload.patientId === patientId) {
      if (enableDebugLogging !== false) {
        console.log('[SignalR] ✅ Executing file changed refresh');
      }
      onFileChanged?.(payload);
      onFileDataChanged?.();
    }
  }, []);

  const handlePatientChanged = useCallback((payload: { patientId?: number; action?: string }) => {
    const { patientId, onPatientChanged, onPatientDataChanged, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] patientChanged event received:', {
        payload,
        currentPatientId: patientId,
        hasSpecificHandler: !!onPatientChanged,
        hasGenericHandler: !!onPatientDataChanged,
      });
    }

    if (!payload || patientId === undefined || payload.patientId === patientId) {
      if (enableDebugLogging !== false) {
        console.log('[SignalR] ✅ Executing patient changed refresh');
      }
      onPatientChanged?.(payload);
      onPatientDataChanged?.();
    }
  }, []);

  const handleExamChanged = useCallback((payload: { patientId?: number; examId?: number; action?: string; status?: string }) => {
    const { patientId, examId, onExamChanged, onExamDataChanged, onPatientDataChanged, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] examChanged event received:', {
        payload,
        currentPatientId: patientId,
        currentExamId: examId,
        hasSpecificHandler: !!onExamChanged,
        hasExamGenericHandler: !!onExamDataChanged,
        hasPatientGenericHandler: !!onPatientDataChanged,
      });
    }

    const shouldExecute = !payload || patientId === undefined || payload.patientId === patientId || examId === undefined || payload.examId === examId;

    if (shouldExecute) {
      if (enableDebugLogging !== false) {
        console.log('[SignalR] ✅ Executing exam changed refresh');
      }
      onExamChanged?.(payload);
      onExamDataChanged?.();
      onPatientDataChanged?.(); // Exams are part of patient data
    }
  }, []);

  const handleSystemDataChanged = useCallback((payload: { dataType?: string; worklistId?: number; timestamp?: string }) => {
    const { worklistId, onSystemDataChanged, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] systemDataChanged event received:', {
        payload,
        currentWorklistId: worklistId,
        hasHandler: !!onSystemDataChanged,
      });
    }

    // System data changes are usually global, but can be filtered by worklist
    const shouldExecute = !payload?.worklistId || worklistId === undefined || payload.worklistId === worklistId;

    if (shouldExecute) {
      if (enableDebugLogging !== false) {
        console.log('[SignalR] ✅ Executing system data changed refresh');
      }
      onSystemDataChanged?.(payload);
    }
  }, []);

  const handleLocationChanged = useCallback((payload: { locationId?: number; action?: string }) => {
    const { locationId, onLocationChanged, onLocationDataChanged, onSystemDataChanged, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] locationChanged event received:', {
        payload,
        currentLocationId: locationId,
        hasSpecificHandler: !!onLocationChanged,
        hasLocationGenericHandler: !!onLocationDataChanged,
        hasSystemGenericHandler: !!onSystemDataChanged,
      });
    }

    if (!payload || locationId === undefined || payload.locationId === locationId) {
      if (enableDebugLogging !== false) {
        console.log('[SignalR] ✅ Executing location changed refresh');
      }
      onLocationChanged?.(payload);
      onLocationDataChanged?.();
    }

    // Always trigger system data refresh for location changes
    onSystemDataChanged?.({ dataType: 'locations', timestamp: new Date().toISOString() });
  }, []);

  const handleFormChanged = useCallback((payload: { patientId?: number; formId?: number; action?: string }) => {
    const { patientId, onFormChanged, onPatientDataChanged, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] formChanged event received:', {
        payload,
        currentPatientId: patientId,
        hasSpecificHandler: !!onFormChanged,
        hasGenericHandler: !!onPatientDataChanged,
      });
    }

    if (!payload || patientId === undefined || payload.patientId === patientId) {
      if (enableDebugLogging !== false) {
        console.log('[SignalR] ✅ Executing form changed refresh');
      }
      onFormChanged?.(payload);
      onPatientDataChanged?.();
    }
  }, []);

  const handleUserPermissionsChanged = useCallback((payload: { userId?: string }) => {
    const { onUserPermissionsChanged, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] userPermissionsChanged event received:', {
        payload,
        hasHandler: !!onUserPermissionsChanged,
      });
    }

    // User permissions changes are typically for the current user
    if (enableDebugLogging !== false) {
      console.log('[SignalR] ✅ Executing user permissions changed refresh');
    }
    onUserPermissionsChanged?.(payload);
  }, []);

  const handleAIStatusUpdate = useCallback(
    (payload: {
      id: string;
      type: string;
      status: string;
      patientId?: number;
      fileId?: number;
      templateId?: number;
      worklistId?: number;
      progress?: number;
      message?: string;
      errorMessage?: string;
      startTime?: string;
      endTime?: string;
      result?: any;
      timestamp: string;
    }) => {
      const { patientId, onAIStatusUpdate, enableDebugLogging } = optionsRef.current;
      if (enableDebugLogging !== false) {
        console.log('[SignalR] aiStatusUpdate event received:', {
          payload,
          currentPatientId: patientId,
          hasHandler: !!onAIStatusUpdate,
        });
      }

      // Filter by patient ID if specified
      if (patientId !== undefined && payload.patientId !== undefined && payload.patientId !== patientId) {
        return;
      }

      onAIStatusUpdate?.(payload);
    },
    [],
  );

  // Legacy upload status handler
  const handleUploadStatus = useCallback((status: string, payload?: { patientId?: number; fileId?: number }) => {
    const { patientId, onFileDataChanged, enableDebugLogging } = optionsRef.current;
    if (enableDebugLogging !== false) {
      console.log('[SignalR] uploadStatus event received:', {
        status,
        payload,
        currentPatientId: patientId,
        hasGenericHandler: !!onFileDataChanged,
      });
    }

    // Trigger file data refresh for upload status changes
    if (!payload || patientId === undefined || payload.patientId === patientId) {
      if (enableDebugLogging !== false) {
        console.log('[SignalR] ✅ Executing upload status refresh');
      }
      onFileDataChanged?.();
    }
  }, []);

  // Set up event listeners
  useEffect(() => {
    if (!connection) {
      if (options.enableDebugLogging !== false) {
        console.log('[SignalR] Cannot register event listeners - no connection');
      }
      return;
    }

    if (options.enableDebugLogging !== false) {
      console.log('[SignalR] Registering event listeners for patient:', options.patientId, 'location:', options.locationId);
    }

    // Register legacy event handlers (for backward compatibility)
    connection.on('patientFileUploaded', handlePatientFileUploaded);
    connection.on('worklistUpdated', handleWorklistUpdated);
    connection.on('formUpdated', handleFormUpdated);
    connection.on('messageReceived', handleMessageReceived);
    connection.on('dataUpdated', handleDataUpdated);
    connection.on('uploadStatus', handleUploadStatus);

    // Register NEW core real-time event handlers
    connection.on('fileChanged', handleFileChanged);
    connection.on('patientChanged', handlePatientChanged);
    connection.on('examChanged', handleExamChanged);
    connection.on('systemDataChanged', handleSystemDataChanged);
    connection.on('locationChanged', handleLocationChanged);
    connection.on('formChanged', handleFormChanged);
    connection.on('userPermissionsChanged', handleUserPermissionsChanged);
    connection.on('aiStatusUpdate', handleAIStatusUpdate);

    if (options.enableDebugLogging !== false) {
      console.log('[SignalR] ✅ Event listeners registered successfully');
    }

    return () => {
      if (options.enableDebugLogging !== false) {
        console.log('[SignalR] Cleaning up event listeners for patient:', options.patientId, 'location:', options.locationId);
      }

      // Clean up legacy event listeners
      connection.off('patientFileUploaded', handlePatientFileUploaded);
      connection.off('worklistUpdated', handleWorklistUpdated);
      connection.off('formUpdated', handleFormUpdated);
      connection.off('messageReceived', handleMessageReceived);
      connection.off('dataUpdated', handleDataUpdated);
      connection.off('uploadStatus', handleUploadStatus);

      // Clean up NEW event listeners
      connection.off('fileChanged', handleFileChanged);
      connection.off('patientChanged', handlePatientChanged);
      connection.off('examChanged', handleExamChanged);
      connection.off('systemDataChanged', handleSystemDataChanged);
      connection.off('locationChanged', handleLocationChanged);
      connection.off('formChanged', handleFormChanged);
      connection.off('userPermissionsChanged', handleUserPermissionsChanged);
      connection.off('aiStatusUpdate', handleAIStatusUpdate);
    };
  }, [
    connection,
    handlePatientFileUploaded,
    handleWorklistUpdated,
    handleFormUpdated,
    handleMessageReceived,
    handleDataUpdated,
    handleUploadStatus,
    handleFileChanged,
    handlePatientChanged,
    handleExamChanged,
    handleSystemDataChanged,
    handleLocationChanged,
    handleFormChanged,
    handleUserPermissionsChanged,
    handleAIStatusUpdate,
    options.patientId,
    options.locationId,
    options.enableDebugLogging,
  ]);

  // Helper function to send events
  const sendEvent = useCallback(
    async (eventName: string, payload: unknown) => {
      if (connection) {
        try {
          if (options.enableDebugLogging !== false) {
            console.log(`[SignalR] Sending ${eventName}:`, payload);
          }
          await connection.send(eventName, payload);
          if (options.enableDebugLogging !== false) {
            console.log(`[SignalR] ✅ Successfully sent ${eventName}`);
          }
        } catch (error) {
          console.error(`[SignalR] ❌ Failed to send ${eventName}:`, error);
        }
      } else {
        console.warn(`[SignalR] ❌ Cannot send ${eventName} - no connection available`);
      }
    },
    [connection, options.enableDebugLogging],
  );

  // Helper function to refresh user context
  const refreshUserContext = useCallback(async () => {
    if (connection) {
      try {
        if (options.enableDebugLogging !== false) {
          console.log('[SignalR] Refreshing user context');
        }
        await connection.send('RefreshUserContext');
        if (options.enableDebugLogging !== false) {
          console.log('[SignalR] ✅ User context refreshed successfully');
        }
      } catch (error) {
        console.error('[SignalR] ❌ Failed to refresh user context:', error);
      }
    } else {
      console.warn('[SignalR] ❌ Cannot refresh user context - no connection available');
    }
  }, [connection, options.enableDebugLogging]);

  return {
    sendEvent,
    refreshUserContext,
    isConnected: !!connection,
    connectionState: connection?.state,
    connectionId: connection?.connectionId,
    isViewingPatient: isViewingPatientRef.current,
  };
};
